<script>
    // Packages
    import {ref} from 'vue';
    import moment from "moment";
    import {emitter} from "@/eventHub";
    import {useForm, usePage} from '@inertiajs/vue3';
    import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
    
    // Components
    import SlideOutErrors from "@/Components/SlideOutErrors";
    
    // Icons
    import {XIcon} from '@heroicons/vue/outline';

    export default {
        components: {
            XIcon,
            Dialog,
            DialogTitle,
            DialogOverlay,
            TransitionRoot,
            SlideOutErrors,
            TransitionChild
        },
        props: {
            fixture: Object,
            quote: Object
        },
        data() {
            return {
                show: ref(false),
                updating: false,
                showErrors: ref(false),
                form: useForm({
                    amount: null,
                    invoice_id: null,
                    type: null,
                }),
                invoice: null,
                currency: null
            }
        },
        created() {
            this.moment = moment
        },
        methods: {
            submit() {
                console.log('HIT');
                this.$swal.fire({
                    title: 'Cancel Invoice',
                    html: "You are cancelling an invoice. <br/>Are you sure you'd like to do this?",
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No'
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Delete the invoice
                        var url = '';
                        var routeParameters = [this.form.invoice_id];

                        switch(this.form.type) {
                            case 'Management Fee':
                                url = 'frontend.management-fees.cancel';
                                break;
                            case 'Invoice':
                                url = 'frontend.invoices.cancel';
                                break;
                            case 'Commission':
                                url = 'frontend.fixture.commission.cancel';
                                routeParameters.unshift({ id: usePage().props.fixture.id });
                                break;
                        }

                        this.form.post(route(url, routeParameters), {
                            onSuccess: page => {
                                this.show = false;
                                this.showErrors = false;
                                this.form.reset();
                            },
                            onError: errors => {
                                this.showErrors = true;
                            },
                            preserveScroll: true
                        });
                    }
                });
            },
            close() {
                this.form.reset();
                this.form.clearErrors();
                this.show = false;
            },
            open(invoice, invoice_type) {
                this.show = true;
                this.form.clearErrors();
                this.form.amount = invoice.amount
                this.form.invoice_id = invoice.id;
                this.form.type = invoice_type;
                this.getConfig();
            },
            getConfig() {
                var url = '';
                switch(this.form.type) {
                    case 'Management Fee':
                        url = 'frontend.management-fees.configuration';
                        break;
                    case 'Invoice':
                        url = 'frontend.fixture.invoice.configuration';
                        break;
                    case 'Commission':
                        url = 'frontend.fixture.commission.invoice_configuration';
                        break;
                }
                axios.get(route(url, [usePage().props.fixture.id, this.form.invoice_id])).then(result => {
                    // loop over each key returned from the config json
                    this.invoice = result.data['invoice'];
                    this.currency = result.data['currency'];
                });
            }
        }
    }
</script>

<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl" @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Create Credit Note
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Enter the amount that you would like to credit within the below area to create your credit note.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div class="rounded-md bg-red-50 p-4">
                                                    <div class="flex">
                                                        <p class="text-sm text-red-700">
                                                            <strong>You are crediting an invoice in full.</strong><br />
                                                            Please review the below information to ensure you're happy you're crediting this invoice in full.
                                                        </p>
                                                    </div>
                                                </div>

                                                <div v-if="invoice" class="text-gray-900 text-sm">
                                                    <div class="mt-4 grid grid-cols-2 gap-4">
                                                        <div class="font-bold">
                                                            Reference
                                                        </div>
                                                        <div class="text-right">
                                                            {{ invoice.reference }}
                                                        </div>
                                                    </div>
                                                    <div class="mt-4 grid grid-cols-2 gap-4">
                                                        <div class="font-bold">
                                                            Date Raised
                                                        </div>
                                                        <div class="text-right">
                                                            {{ moment(invoice.invoiced_at, 'DD-MM-YYYY').format('DD MMM YYYY') }}
                                                        </div>
                                                    </div>
                                                    <div class="mt-4 grid grid-cols-2 gap-4">
                                                        <div class="font-bold">
                                                            Date Due
                                                        </div>
                                                        <div class="text-right">
                                                            {{ moment(invoice.due_at, 'DD-MM-YYYY').format('DD MMM YYYY') }}
                                                        </div>
                                                    </div>
                                                    <div class="mt-4 grid grid-cols-2 gap-4 text-sm">
                                                        <div class="font-bold" v-if="form.type == 'Invoice' && invoice.quote">
                                                            {{ invoice.quote.fixture.title }} - {{ invoice.quote.name }}
                                                        </div>
                                                        <div class="font-bold" v-if="form.type == 'Commission'">
                                                            {{ invoice.organisation.registered_legal_name }} - {{ invoice.po_reference }}
                                                        </div>
                                                        <div class="text-right" v-if="form.type == 'Invoice' && invoice.quote">
                                                            {{ formatCurrency((invoice.management_fee_included > 0 ? invoice.amount - invoice.management_fee_total : invoice.amount), invoice.quote.sell_currency.code, true) }}
                                                        </div>
                                                        <div class="text-right" v-if="form.type == 'Commission'">
                                                            {{ formatCurrency(invoice.amount, this.currency.code, true) }}
                                                        </div>
                                                        <div class="text-right" v-if="form.type == 'Management Fee'">
                                                            {{ formatCurrency(invoice.amount, invoice.currency?.code ?? 'gbp', true) }}
                                                        </div>
                                                    </div>
                                                    <div class="mt-4 grid grid-cols-2 gap-4 text-sm" v-if="invoice.management_fee_included">
                                                        <div class="font-bold">
                                                            Management Fee
                                                        </div>
                                                        <div class="text-right">
                                                            {{ formatCurrency(invoice.management_fee_total, invoice.quote.sell_currency.code, true) }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <input type="hidden" name="invoice-type" id="invoice-type" v-model="form.type" />
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span>Cancel Invoice</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>